import { useTranslation } from 'react-i18next';
import { normalizeMilestoneStatus, workStatusPillOptions } from './projects/milestoneStatusModel';
import StatusPillSelect, { type StatusPillOption } from './common/StatusPillSelect';
import type { MilestoneStatus } from '../types';

/**
 * P-53 (D-225 QX-443) - the work status (planning / in progress / review / testing / completed) as ONE
 * status pill with a dropdown list (`StatusPillSelect`), instead of the old row of five circles.
 * Same props and same rules as before: a pick calls `onChange`; without `onChange` (no permission) or
 * with `disabled` it is the plain read-only status badge. The file name is kept so every screen that
 * showed the circles (milestones, tasks, the timer's progress field) gets the pill with no other change.
 */
export default function StatusCircles({ status, onChange, disabled = false, pending = false, label, className = '' }: {
  status: string | null | undefined;
  /** Absent -> read-only (no permission). */
  onChange?: (status: MilestoneStatus) => void;
  disabled?: boolean;
  /** A change is being saved (the pill stays visible, not clickable). */
  pending?: boolean;
  /** Accessible name of the control (e.g. "סטטוס של פיתוח"). */
  label?: string;
  /** Kept for callers that still pass it; the pill has one size. */
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { t } = useTranslation();
  const current = normalizeMilestoneStatus(status);
  const options: StatusPillOption<MilestoneStatus>[] = workStatusPillOptions((s) => t(`milestoneStatus.${s}`));
  const name = label ?? t('statusCircles.label');
  return (
    <span className={`inline-flex ${className}`} data-testid="status-circles">
      <StatusPillSelect<MilestoneStatus>
        value={current}
        options={options}
        onChange={(s) => onChange?.(s)}
        label={`${name}: ${t(`milestoneStatus.${current}`)}`}
        sheetTitle={name}
        disabled={!onChange || disabled}
        pending={pending}
      />
    </span>
  );
}
